import { useState, useEffect, useMemo } from "react";
import { useRestaurant } from "../context/RestaurantContext";
import { api } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Loader2, 
  Upload, 
  Wallet,
  Calendar,
  TrendingUp,
  Trash2,
  Plus,
  FileText,
  DollarSign
} from "lucide-react";

export default function ExpensesPage() {
  const { currentRestaurant } = useRestaurant();
  const restaurantId = currentRestaurant?.id || localStorage.getItem("selectedRestaurantId");
  
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  
  // Filter state
  const [dateFilter, setDateFilter] = useState("month"); // "week", "month", "quarter", "custom"
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Add expense form
  const [formData, setFormData] = useState({
    vendor_name: "",
    total_amount: "",
    gst_amount: "",
    date: new Date().toISOString().split('T')[0],
    description: "",
    receipt_file: null
  });

  useEffect(() => {
    if (restaurantId) {
      loadExpenses();
    }
  }, [restaurantId]);

  const loadExpenses = async () => {
    try {
      const response = await api.get(`/expenses?restaurant_id=${restaurantId}`);
      setExpenses(response.data);
    } catch (error) {
      console.error("Failed to load expenses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate date range based on filter
  const getDateRange = () => {
    const now = new Date();
    let start, end;
    
    switch (dateFilter) {
      case "week":
        start = new Date(now);
        start.setDate(now.getDate() - 7);
        end = now;
        break;
      case "month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case "quarter":
        start = new Date(now);
        start.setMonth(now.getMonth() - 3);
        end = now;
        break;
      case "custom":
        start = startDate ? new Date(startDate) : new Date(0);
        end = endDate ? new Date(endDate) : now;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
    }
    
    return { start, end };
  };

  // Filter expenses by date range
  const filteredExpenses = useMemo(() => {
    const { start, end } = getDateRange();
    return expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate >= start && expenseDate <= end;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [expenses, dateFilter, startDate, endDate]);

  // Calculate totals
  const totals = useMemo(() => {
    return filteredExpenses.reduce((acc, expense) => ({
      total: acc.total + (expense.total_amount || 0),
      gst: acc.gst + (expense.gst_amount || 0),
      count: acc.count + 1
    }), { total: 0, gst: 0, count: 0 });
  }, [filteredExpenses]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, receipt_file: file }));
    }
  };

  const handleAddExpense = async () => {
    if (!formData.vendor_name || !formData.total_amount) {
      toast.error("Please enter vendor name and total amount");
      return;
    }

    setIsUploading(true);
    try {
      const data = new FormData();
      data.append("restaurant_id", restaurantId);
      data.append("vendor_name", formData.vendor_name);
      data.append("total_amount", parseFloat(formData.total_amount));
      data.append("gst_amount", parseFloat(formData.gst_amount) || 0);
      data.append("date", formData.date);
      data.append("description", formData.description);
      if (formData.receipt_file) {
        data.append("file", formData.receipt_file);
      }

      await api.post("/expenses", data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Expense added successfully");
      setShowAddDialog(false);
      setFormData({
        vendor_name: "",
        total_amount: "",
        gst_amount: "",
        date: new Date().toISOString().split('T')[0],
        description: "",
        receipt_file: null
      });
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add expense");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!deleteExpenseId) return;
    
    try {
      await api.delete(`/expenses/${deleteExpenseId}`);
      toast.success("Expense deleted");
      loadExpenses();
    } catch (error) {
      toast.error("Failed to delete expense");
    } finally {
      setDeleteExpenseId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <AppLayout title="Expenses" showBack>
      <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-4">
        
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-emerald-700 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs font-medium">Total Spent</span>
              </div>
              <p className="text-xl font-bold text-emerald-900">{formatCurrency(totals.total)}</p>
              <p className="text-xs text-emerald-600">{totals.count} expense{totals.count !== 1 ? 's' : ''}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs font-medium">Total GST</span>
              </div>
              <p className="text-xl font-bold text-amber-900">{formatCurrency(totals.gst)}</p>
              <p className="text-xs text-amber-600">Tax paid</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter Bar */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-32 h-9">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="quarter">3 Months</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              
              {dateFilter === "custom" && (
                <>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-32 h-9"
                  />
                  <span className="text-stone-400">to</span>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-32 h-9"
                  />
                </>
              )}
              
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="ml-auto h-9 bg-kale-600 hover:bg-kale-700"
                data-testid="add-expense-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Expenses List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : filteredExpenses.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Wallet className="w-12 h-12 mx-auto text-stone-300 mb-3" />
              <p className="text-stone-500">No expenses found for this period</p>
              <Button 
                onClick={() => setShowAddDialog(true)}
                className="mt-4 bg-kale-600 hover:bg-kale-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Expense
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filteredExpenses.map((expense) => (
              <Card key={expense.id} className="card-hover" data-testid={`expense-${expense.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-stone-900">{expense.vendor_name}</h4>
                        {expense.has_receipt && (
                          <Badge variant="secondary" className="text-xs bg-kale-100 text-kale-700">
                            <FileText className="w-3 h-3 mr-1" />
                            Receipt
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-500">{formatDate(expense.date)}</p>
                      {expense.description && (
                        <p className="text-xs text-stone-400 mt-1">{expense.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-stone-900">{formatCurrency(expense.total_amount)}</p>
                      {expense.gst_amount > 0 && (
                        <p className="text-xs text-stone-500">GST: {formatCurrency(expense.gst_amount)}</p>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setDeleteExpenseId(expense.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Expense Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-md" data-testid="add-expense-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-kale-600" />
              Add Expense
            </DialogTitle>
            <DialogDescription>
              Record a new expense. Optionally attach a receipt.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor / Supplier *</Label>
              <Input
                id="vendor"
                value={formData.vendor_name}
                onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                placeholder="e.g., Costco, Cash and Carry"
                className="input-mobile"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="total">Total Amount *</Label>
                <Input
                  id="total"
                  type="number"
                  step="0.01"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  placeholder="0.00"
                  className="input-mobile"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst">GST Amount</Label>
                <Input
                  id="gst"
                  type="number"
                  step="0.01"
                  value={formData.gst_amount}
                  onChange={(e) => setFormData({ ...formData, gst_amount: e.target.value })}
                  placeholder="0.00"
                  className="input-mobile"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="input-mobile"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Weekly grocery run"
                className="input-mobile"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt">Attach Receipt (optional)</Label>
              <Input
                id="receipt"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleFileChange}
                className="input-mobile"
              />
              {formData.receipt_file && (
                <p className="text-xs text-kale-600">
                  Selected: {formData.receipt_file.name}
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1 h-11">
              Cancel
            </Button>
            <Button 
              onClick={handleAddExpense} 
              className="flex-1 btn-primary" 
              disabled={isUploading}
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteExpenseId} onOpenChange={() => setDeleteExpenseId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Expense?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this expense record. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteExpense} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
